// Live USD → user-currency conversion using exchangerate.host (free, no key).
// Cached for 24h in localStorage so we don't hammer the endpoint on every
// keystroke during event creation.
import { useEffect, useState } from 'react';

const CACHE_PREFIX = 'rivl:fx:';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const ENDPOINT = 'https://api.exchangerate.host/latest';

function readCache(code) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + code);
    if (!raw) return null;
    const { rate, at } = JSON.parse(raw);
    if (Date.now() - at > CACHE_TTL_MS) return null;
    return Number(rate);
  } catch {
    return null;
  }
}

function writeCache(code, rate) {
  try {
    localStorage.setItem(CACHE_PREFIX + code, JSON.stringify({ rate, at: Date.now() }));
  } catch {
    /* ignore */
  }
}

/**
 * Returns { rate, loading } for USD → `target`. If `target === 'USD'` the
 * rate is 1. If the network call fails we fall back to a small set of
 * sane hard-coded rates so the UI never shows "—" for common currencies.
 */
const FALLBACK = {
  USD: 1,
  NGN: 1650,
  GHS: 13,
  KES: 130,
  ZAR: 18.5,
  EGP: 49,
  EUR: 0.92,
  GBP: 0.79,
  CAD: 1.36,
  AUD: 1.52,
  JPY: 156,
  INR: 84,
  BRL: 5.6,
};

export function useFxRate(target) {
  const [state, setState] = useState(() => {
    if (!target || target === 'USD') return { rate: 1, loading: false };
    const cached = readCache(target);
    return { rate: cached ?? FALLBACK[target] ?? 1, loading: cached == null };
  });

  useEffect(() => {
    if (!target || target === 'USD') { setState({ rate: 1, loading: false }); return; }
    let cancelled = false;
    const cached = readCache(target);
    if (cached != null) { setState({ rate: cached, loading: false }); return; }
    // Network fetch
    (async () => {
      try {
        const res = await fetch(`${ENDPOINT}?base=USD&symbols=${target}`);
        const json = await res.json();
        const rate = json?.rates?.[target];
        if (!cancelled && Number.isFinite(rate)) {
          writeCache(target, rate);
          setState({ rate, loading: false });
          return;
        }
        if (!cancelled) setState({ rate: FALLBACK[target] || 1, loading: false });
      } catch {
        if (!cancelled) setState({ rate: FALLBACK[target] || 1, loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [target]);

  return state;
}

/**
 * Format a USD amount into the user's currency string.
 *   formatPriceUSD(30, { code: 'NGN', symbol: '₦' }, 1650) => "₦49,500"
 *   formatPriceUSD(30, { code: 'USD', symbol: '$' }, 1)    => "$30"
 *   formatPriceUSD(0, ...) => "Free"
 *   formatPriceUSD(null, ...) => "Contact sales"
 */
export function formatPriceUSD(usd, currency, rate) {
  if (usd === 0) return 'Free';
  if (usd == null) return 'Contact sales';
  const local = Math.round(Number(usd) * (Number(rate) || 1));
  const formatted = local.toLocaleString('en-US');
  return `${currency.symbol}${formatted}`;
}
